import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';

export const GENERIC_ERROR = 'Your bid could not be submitted. Please try again.';

// Owns the confirm -> submit -> outcome lifecycle shared by the two bid forms.
// onSuccess is optional and fires only for a payload carrying a preBid and no
// errors, so a failed submission leaves the form open for another attempt.
const useBidSubmission = ({
  mutation,
  mutationOptions = {},
  payloadKey,
  onSuccess,
}) => {
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState(null);

  const [runMutation, { loading: submitting }] = useMutation(
    mutation, mutationOptions
  );

  const requestSubmit = useCallback((variables, message) => {
    setError(null);
    setConfirmation({ variables, message });
  }, []);

  const cancel = useCallback(() => setConfirmation(null), []);

  const confirm = useCallback(() => {
    if (!confirmation) return;

    const { variables } = confirmation;
    setConfirmation(null);

    runMutation({ variables })
      .then(({ data }) => {
        const payload = data?.[payloadKey];
        const failure = payload?.errors?.[0];

        if (failure) {
          setError(failure.message);
        } else if (!payload?.preBid) {
          setError(GENERIC_ERROR);
        } else if (onSuccess) {
          // The whole variables object, so callers can vary copy by bid type.
          onSuccess(variables);
        }
      })
      .catch(() => setError(GENERIC_ERROR));
  }, [confirmation, runMutation, payloadKey, onSuccess]);

  return { requestSubmit, confirmation, confirm, cancel, submitting, error };
};

export default useBidSubmission;
